import { createContext, useContext, type ReactNode } from 'react';

/**
 * API クライアントの契約。実装は apps/web/src/api/client.ts（fetch + 401 の自動リフレッシュ）。
 * modules/*\/ui はこのインターフェースだけを見る（apps/ には依存しない）。
 */
export type QueryValue = string | number | boolean | null | undefined;
export type Query = Record<string, QueryValue>;
export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** 401 でもリフレッシュを試みない（ログイン・リフレッシュ自身） */
  skipAuthRefresh?: boolean;
  /** ページを閉じる途中でも送り切る（fetch の keepalive。pagehide での通話の退出など・本文は小さいものだけ） */
  keepalive?: boolean;
}

export interface Downloaded {
  blob: Blob;
  filename: string | null;
}

export interface ApiClient {
  request<T>(method: HttpMethod, path: string, opts?: RequestOptions): Promise<T>;
  get<T>(path: string, query?: Query, opts?: Omit<RequestOptions, 'query' | 'body'>): Promise<T>;
  post<T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body'>): Promise<T>;
  patch<T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body'>): Promise<T>;
  put<T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'body'>): Promise<T>;
  del<T>(path: string, opts?: RequestOptions): Promise<T>;
  /** CSV などのファイル取得（Authorization 付き fetch → Blob） */
  download(path: string, query?: Query): Promise<Downloaded>;
}

const ApiContext = createContext<ApiClient | null>(null);

export function ApiProvider({ client, children }: { client: ApiClient; children: ReactNode }) {
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiClient {
  const c = useContext(ApiContext);
  if (!c) throw new Error('ApiProvider の外で useApi() が呼ばれました');
  return c;
}

/** クエリ文字列の組み立て（undefined / null / 空文字は捨てる） */
export function buildQuery(query?: Query): string {
  if (!query) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** ブラウザに Blob を保存させる（a[download]） */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
